import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETIQUETA_RESPUESTA_TERNARIA,
  RESPUESTAS_TERNARIAS,
  RESULTADOS_INSPECCION,
  arbolVisible,
  esAlerta,
  esFalla,
  estaVacia,
  firmaSchema,
  formatearFechaHora,
  requiereEvidenciaPorRespuesta,
  type Ejecucion,
  type Firma,
  type ItemCongelado,
  type MapaRespuestas,
} from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { ErroresFormulario } from '../componentes/piezas.js';
import { IndiceSecciones } from '../componentes/indice-secciones.js';
import {
  Aviso,
  Cargando,
  GaleriaImagenes,
  SubidaImagenes,
  Vacio,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import { SelectorFecha, SelectorLista } from '../componentes/selectores.js';
import { useVolver } from '../componentes/boton-volver.js';

/**
 * Ejecución de un checklist en sitio — §16.8.
 *
 * Pensada para el móvil, que es donde ocurre: el técnico responde en el
 * consultorio. Cada respuesta se envía apenas se registra, de modo que un corte
 * de conexión en el campo 200 de 350 cueste un campo y no la jornada.
 */
export function EjecucionChecklist(): JSX.Element {
  const { id = '' } = useParams();
  const cliente = useQueryClient();
  const navegar = useNavigate();
  const [guardando, setGuardando] = useState<string | null>(null);
  const [guardado, setGuardado] = useState<string | null>(null);

  const { data: ejecucion, isLoading } = useQuery({
    queryKey: ['ejecucion', id],
    queryFn: () => api.get<Ejecucion>(`/ejecuciones/${id}`),
  });

  const responder = useMutation({
    mutationFn: (entrada: { idCongelado: string; valor: unknown; nota?: string; adjuntos?: string[] }) =>
      api.post<Ejecucion>(`/ejecuciones/${id}/respuestas`, { ...entrada, adjuntos: entrada.adjuntos ?? [] }),
    onMutate: (entrada) => {
      setGuardando(entrada.idCongelado);
      setGuardado(null);
    },
    onSuccess: (actualizada, entrada) => {
      // Se escribe el resultado del servidor en la caché en lugar de invalidar:
      // recargar el checklist entero a cada respuesta haría perder el scroll en
      // una plantilla de 350 campos.
      cliente.setQueryData(['ejecucion', id], actualizada);
      setGuardado(entrada.idCongelado);
    },
    onSettled: () => setGuardando(null),
  });

  const completar = useMutation({
    mutationFn: () => api.post<Ejecucion>(`/ejecuciones/${id}/completar`),
    onSuccess: async (actualizada) => {
      cliente.setQueryData(['ejecucion', id], actualizada);
      await cliente.invalidateQueries({ queryKey: ['orden', actualizada.ordenTrabajoId] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', actualizada.ordenTrabajoId] });
      await cliente.invalidateQueries({ queryKey: ['ordenes'] });
      // Con `desdeEjecucion`, no rebota de vuelta al checklist recién
      // completado: lo que sigue es cerrar la OT desde su ficha. `replace`,
      // no push: se llegó acá por un redirect automático (§ pedido, único
      // procedimiento de la OT) que ya reemplazó la entrada de la OT en el
      // historial — empujar una nueva dejaba el checklist ya completado
      // como destino del «atrás» del navegador, en vez de saltarlo.
      navegar(`/ordenes/${actualizada.ordenTrabajoId}`, { replace: true, state: { desdeEjecucion: true } });
    },
  });

  // Destino de reserva si no hay `ejecucion` todavía (primer render, antes de
  // que responda la query): no importa, `irAtras` no se usa hasta que exista.
  // `desdeEjecucion` le avisa a la ficha de la OT que no vuelva a saltar acá
  // de una: si no, «Atrás» rebotaría sin parar entre las dos pantallas cuando
  // la OT tiene un único procedimiento adjunto (§ pedido).
  const irAtras = useVolver(ejecucion ? `/ordenes/${ejecucion.ordenTrabajoId}` : '/ordenes', { desdeEjecucion: true });

  if (isLoading) return <Cargando />;
  if (!ejecucion) return <Vacio titulo="La ejecución no existe" />;

  const respuestas: MapaRespuestas = new Map(ejecucion.respuestas.map((r) => [r.idCongelado, { valor: r.valor }]));
  // Aparte de `respuestas` (tipo compartido con el servidor, solo `valor`):
  // la nota es un detalle propio de esta pantalla, no de la evaluación del
  // árbol, así que viaja en su propio mapa en vez de ensanchar ese tipo.
  const notas = new Map(ejecucion.respuestas.map((r) => [r.idCongelado, r.nota]));
  const visible = arbolVisible(ejecucion.estructura.items, respuestas);
  const { resumen } = ejecucion;
  const respondidos = resumen.totalVisibles - resumen.sinResponder;
  const avance = resumen.totalVisibles > 0 ? (respondidos / resumen.totalVisibles) * 100 : 0;
  const cerrada = ejecucion.estado === 'COMPLETADA';

  return (
    <div className="ejecucion-pagina">
      <div className="ejecucion-cabecera" style={{ gridColumn: '1 / -1' }}>
        <button type="button" onClick={irAtras} className="ejecucion-volver">
          ← Volver a la orden de trabajo
        </button>
        <h2>{ejecucion.plantillaNombre}</h2>
        <div className="barra">
          <div className="barra-relleno" style={{ width: `${avance}%` }} />
        </div>
        <div className="ejecucion-progreso">
          {respondidos} de {resumen.totalVisibles} campos
          {resumen.falla > 0 ? ` · ${resumen.falla} FALLA detectada${resumen.falla === 1 ? '' : 's'}` : ''}
          {resumen.alerta > 0 ? ` · ${resumen.alerta} alerta${resumen.alerta === 1 ? '' : 's'}` : ''}
        </div>
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
      {completar.isError ? <ErroresFormulario error={completar.error} /> : null}
      {responder.isError ? <ErroresFormulario error={responder.error} /> : null}

      {cerrada ? (
        <Aviso tono="exito">
          Checklist completado
          {ejecucion.puntuacionActiva && ejecucion.porcentaje
            ? `. Puntaje: ${ejecucion.puntajeTotal} (${ejecucion.porcentaje} %)`
            : '.'}
        </Aviso>
      ) : null}

      {/* Antes de confirmar, el técnico ve qué trabajo va a generar su cierre. */}
      {!cerrada && (resumen.falla > 0 || resumen.alerta > 0) ? (
        <Aviso tono="atencion">
          Al cerrar se generarán{' '}
          {resumen.falla > 0 ? `${resumen.falla} orden(es) correctiva(s) por falla` : ''}
          {resumen.falla > 0 && resumen.alerta > 0 ? ' y ' : ''}
          {resumen.alerta > 0 ? `una orden agrupando ${resumen.alerta} alerta(s)` : ''}.
        </Aviso>
      ) : null}
      </div>

      <IndiceSecciones
        secciones={visible.flatMap((it) =>
          it.tipoItem === 'SECCION' ? [{ id: `seccion-${it.idCongelado}`, nombre: it.nombre }] : [],
        )}
      />
      <div className="panel">
          <div className="panel-cuerpo">
            <Nivel
              items={visible}
              respuestas={respuestas}
              notas={notas}
              cerrada={cerrada}
              guardando={guardando}
              guardado={guardado}
              onResponder={(idCongelado, valor, nota, adjuntos) =>
                responder.mutate({ idCongelado, valor, nota, adjuntos })
              }
            />
          </div>

          {!cerrada ? (
            <div className="paginacion" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="boton"
                disabled={completar.isPending || resumen.requeridosPendientes > 0}
                onClick={() => completar.mutate()}
              >
                {resumen.requeridosPendientes > 0
                  ? `Faltan ${resumen.requeridosPendientes} campo(s) obligatorio(s)`
                  : 'Completar y cerrar checklist'}
              </button>
            </div>
          ) : null}
        </div>
    </div>
  );
}

function Nivel({
  items,
  respuestas,
  notas,
  cerrada,
  guardando,
  guardado,
  onResponder,
}: {
  items: ItemCongelado[];
  respuestas: MapaRespuestas;
  notas: Map<string, string | null>;
  cerrada: boolean;
  guardando: string | null;
  guardado: string | null;
  onResponder: (idCongelado: string, valor: unknown, nota?: string, adjuntos?: string[]) => void;
}): JSX.Element {
  return (
    <>
      {items.map((item) => {
        if (item.tipoItem === 'SECCION' || item.tipoItem === 'ENCABEZADO') {
          return (
            <div key={item.idCongelado} id={`seccion-${item.idCongelado}`}>
              <div className="seccion-ejecucion">{item.nombre}</div>
              {item.instrucciones ? <div className="campo-instrucciones">{item.instrucciones}</div> : null}
              <Nivel
                items={item.hijos}
                respuestas={respuestas}
                notas={notas}
                cerrada={cerrada}
                guardando={guardando}
                guardado={guardado}
                onResponder={onResponder}
              />
            </div>
          );
        }

        return (
          <div key={item.idCongelado}>
            <CampoEjecucion
              item={item}
              valor={respuestas.get(item.idCongelado)?.valor}
              notaGuardada={notas.get(item.idCongelado) ?? null}
              cerrada={cerrada}
              guardando={guardando === item.idCongelado}
              guardado={guardado === item.idCongelado}
              onResponder={onResponder}
            />
            {item.hijos.length > 0 ? (
              <div style={{ paddingLeft: 14, borderLeft: '2px solid var(--borde)', marginLeft: 6 }}>
                <Nivel
                  items={item.hijos}
                  respuestas={respuestas}
                  notas={notas}
                  cerrada={cerrada}
                  guardando={guardando}
                  guardado={guardado}
                  onResponder={onResponder}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function CampoEjecucion({
  item,
  valor,
  notaGuardada,
  cerrada,
  guardando,
  guardado,
  onResponder,
}: {
  item: ItemCongelado;
  valor: unknown;
  notaGuardada: string | null;
  cerrada: boolean;
  guardando: boolean;
  guardado: boolean;
  onResponder: (idCongelado: string, valor: unknown, nota?: string, adjuntos?: string[]) => void;
}): JSX.Element {
  const sinResponder = valor === undefined || estaVacia(valor);
  // Un check que salió mal (INSPECCION=FALLA, SI_NO_NA=NO) exige foto solo,
  // automáticamente — sin que la plantilla lo haya marcado con
  // `evidenciaObligatoria` — ver `requiereEvidenciaPorRespuesta` (§8.4/§8.9).
  const evidenciaPorRespuestaMala = requiereEvidenciaPorRespuesta(item.tipoCampo, valor);
  const exigeFoto = item.evidenciaObligatoria || evidenciaPorRespuestaMala;
  // Una respuesta que no salió del todo bien (ALERTA, FALLA, o un «No»
  // ternario) admite dejar una observación de lo que se encontró — mismo
  // criterio que la foto automática, más ALERTA (§ pedido).
  const admiteNota = evidenciaPorRespuestaMala || esFalla(item.tipoCampo, valor) || esAlerta(item.tipoCampo, valor);

  const [nota, setNota] = useState(notaGuardada ?? '');
  useEffect(() => setNota(notaGuardada ?? ''), [notaGuardada]);

  return (
    <div className={`campo-ejecucion${item.requerido && sinResponder ? ' requerido-pendiente' : ''}`}>
      <div className="campo-enunciado">
        {item.nombre}
        {item.requerido ? <span className="campo-obligatorio">*</span> : null}
        {exigeFoto ? (
          <span className="campo-obligatorio" title="Requiere foto de evidencia para poder cerrar">
            {' '}
            (foto obligatoria)
          </span>
        ) : null}
      </div>

      {/* §8.4: el criterio con el que se evalúa, junto al campo y en el momento
          de responder, que es cuando el técnico lo necesita. */}
      {item.instrucciones ? (
        <details className="campo-instrucciones">
          <summary>Ver criterio de evaluación</summary>
          <div style={{ marginTop: 6 }}>{item.instrucciones}</div>
        </details>
      ) : null}

      {/* Guía visual para el técnico («así se ve un extintor vigente»), distinta
          de la evidencia que él mismo adjunta al responder. */}
      {(item.imagenesReferencia ?? []).length > 0 ? (
        <div style={{ marginBottom: 10 }}>
          <GaleriaImagenes
            fotos={(item.imagenesReferencia ?? []).map((img) => ({
              ...img,
              tipoMime: '',
              tamanoBytes: 0,
              entidad: null,
              entidadId: null,
              creadoEl: '',
              origenSolicitud: false,
            }))}
          />
        </div>
      ) : null}

      <Control
        item={item}
        valor={valor}
        cerrada={cerrada}
        onResponder={(v, adjuntos) => onResponder(item.idCongelado, v, nota.trim() || undefined, adjuntos)}
      />

      {/* Evidencia fotográfica general: los tipos de campo que no son ya en sí
          mismos una foto (`IMAGEN_ARCHIVO`) necesitan este control aparte
          cuando el ítem la exige o simplemente la admite. */}
      {item.tipoCampo !== 'IMAGEN_ARCHIVO' && (exigeFoto || item.permiteAdjuntos) && !sinResponder ? (
        <EvidenciaCampo
          idCongelado={item.idCongelado}
          valorActual={valor}
          cerrada={cerrada}
          obligatoria={exigeFoto}
          onResponder={(v, adjuntos) => onResponder(item.idCongelado, v, nota.trim() || undefined, adjuntos)}
        />
      ) : null}

      {/* Observación de lo encontrado (§ pedido): aparece solo cuando la
          respuesta la admite (ALERTA/FALLA/No), igual que la foto automática
          de arriba — no hace falta marcar nada al armar la plantilla. */}
      {admiteNota && !sinResponder ? (
        cerrada ? (
          nota ? (
            <p className="campo-nota" style={{ marginTop: 8 }}>
              <strong>Observación:</strong> {nota}
            </p>
          ) : null
        ) : (
          <div style={{ marginTop: 8 }}>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              onBlur={() => {
                if ((notaGuardada ?? '') === nota) return;
                onResponder(item.idCongelado, valor, nota.trim() || undefined);
              }}
              placeholder="Observación de lo encontrado…"
              maxLength={4000}
              rows={2}
              style={{ ...estiloEntrada, minHeight: 70, resize: 'vertical' }}
            />
          </div>
        )
      ) : null}

      <div className={`indicador-guardado${guardado ? ' guardado' : ''}`}>
        {guardando ? 'Guardando…' : guardado ? 'Guardado' : ''}
      </div>
    </div>
  );
}

/** Mismo estilo que cualquier otro campo de texto libre del checklist (§ pedido: que no se vean distintos). */
const estiloEntrada = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--borde)',
  borderRadius: 8,
} as const;

function Control({
  item,
  valor,
  cerrada,
  onResponder,
}: {
  item: ItemCongelado;
  valor: unknown;
  cerrada: boolean;
  onResponder: (valor: unknown, adjuntos?: string[]) => void;
}): JSX.Element {
  switch (item.tipoCampo) {
    case 'INSPECCION':
      return (
        <div className="semaforo">
          {RESULTADOS_INSPECCION.map((resultado) => (
            <button
              key={resultado}
              type="button"
              disabled={cerrada}
              className={`${valor === resultado ? 'activo ' : ''}${resultado.toLowerCase()}`}
              onClick={() => onResponder(resultado)}
            >
              {resultado}
            </button>
          ))}
        </div>
      );

    case 'SI_NO_NA':
      return (
        <div className="semaforo">
          {RESPUESTAS_TERNARIAS.map((r) => (
            <button
              key={r}
              type="button"
              disabled={cerrada}
              className={`${valor === r ? 'activo ' : ''}${r === 'SI' ? 'aprobado' : r === 'NO' ? 'falla' : ''}`}
              onClick={() => onResponder(r)}
            >
              {ETIQUETA_RESPUESTA_TERNARIA[r]}
            </button>
          ))}
        </div>
      );

    case 'CASILLA':
      return (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
          <input
            type="checkbox"
            checked={valor === true}
            disabled={cerrada}
            onChange={(e) => onResponder(e.target.checked)}
          />
          Marcar como realizado
        </label>
      );

    case 'ELECCION_MULTIPLE':
      return (
        <SelectorLista
          id={`eleccion-${item.idCongelado}`}
          valor={typeof valor === 'string' ? valor : ''}
          alCambiar={(v) => onResponder(v)}
          opciones={item.opciones.map((o) => ({ id: o, nombre: o }))}
          textoVacio="Seleccione…"
          deshabilitado={cerrada}
        />
      );

    case 'LISTA_COMPROBACION': {
      // Antes caía en el texto libre: la condición de sus opciones nunca coincidía.
      const marcadas = Array.isArray(valor) ? (valor as string[]) : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.opciones.map((o) => (
            <label key={o} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}>
              <input
                type="checkbox"
                checked={marcadas.includes(o)}
                disabled={cerrada}
                onChange={(e) => onResponder(e.target.checked ? [...marcadas, o] : marcadas.filter((m) => m !== o))}
              />
              {o}
            </label>
          ))}
        </div>
      );
    }

    case 'NUMERICO':
    case 'IMPORTE':
    case 'LECTURA_MEDIDOR':
      return (
        <input
          type="number"
          step="0.001"
          defaultValue={typeof valor === 'number' ? valor : ''}
          disabled={cerrada}
          placeholder={item.tipoCampo === 'IMPORTE' ? 'Importe en soles' : 'Valor medido'}
          onBlur={(e) => {
            // Antes, dejar el campo en blanco y salir no guardaba nada: el
            // valor previo quedaba intacto sin forma de borrar una lectura ya
            // registrada (§ bug reportado).
            onResponder(e.target.value === '' ? null : Number(e.target.value));
          }}
          style={estiloEntrada}
        />
      );

    case 'FECHA':
      return (
        <SelectorFecha
          id={`campo-fecha-${item.idCongelado}`}
          valor={typeof valor === 'string' ? valor : ''}
          alCambiar={(v) => onResponder(v)}
          deshabilitado={cerrada}
        />
      );

    case 'FIRMA':
      return <CampoFirma titulo={item.nombre} valor={valor} cerrada={cerrada} onResponder={(v) => onResponder(v)} />;

    case 'IMAGEN_ARCHIVO':
      return <CampoImagen idCongelado={item.idCongelado} valor={valor} cerrada={cerrada} onResponder={onResponder} />;

    default:
      // Texto, firma y lista de comprobación caen aquí: la captura libre
      // registra el dato mientras los controles ricos llegan con el modo sin
      // conexión, planificado como iteración propia.
      return (
        <textarea
          defaultValue={typeof valor === 'string' ? valor : ''}
          disabled={cerrada}
          placeholder="El texto se ingresará aquí"
          onBlur={(e) => {
            if (e.target.value !== (typeof valor === 'string' ? valor : '')) onResponder(e.target.value);
          }}
          style={{ ...estiloEntrada, minHeight: 70, resize: 'vertical' }}
        />
      );
  }
}

/**
 * Evidencia fotográfica adjunta a un campo que no es en sí mismo una imagen
 * (§8.4/§8.9). Reenvía el valor ya respondido junto con los adjuntos nuevos,
 * en vez de manejar un `valor` propio: el campo de imagen (`CampoImagen`) es
 * la respuesta, esto es solo evidencia adicional sobre otra respuesta.
 */
function EvidenciaCampo({
  idCongelado,
  valorActual,
  cerrada,
  obligatoria,
  onResponder,
}: {
  idCongelado: string;
  valorActual: unknown;
  cerrada: boolean;
  obligatoria: boolean;
  onResponder: (valor: unknown, adjuntos?: string[]) => void;
}): JSX.Element | null {
  const [fotos, setFotos] = useState<AdjuntoImagen[] | null>(null);

  const { data } = useQuery({
    queryKey: ['adjuntos', 'RESPUESTA_CAMPO', idCongelado],
    queryFn: () =>
      api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'RESPUESTA_CAMPO', entidadId: idCongelado })}`),
  });

  const actuales = fotos ?? data ?? [];

  if (cerrada) {
    return <GaleriaImagenes fotos={actuales} />;
  }

  return (
    <>
      {obligatoria && actuales.length === 0 ? (
        <p className="campo-nota" style={{ color: 'var(--falla)' }}>
          Falta una foto de evidencia: es obligatoria para poder cerrar este checklist.
        </p>
      ) : null}
      <SubidaImagenes
        etiqueta="Evidencia fotográfica"
        valor={actuales}
        alCambiar={(nuevas) => {
          setFotos(nuevas);
          onResponder(valorActual, nuevas.map((f) => f.id));
        }}
      />
    </>
  );
}

/**
 * Campo de evidencia fotográfica (§8, `IMAGEN_ARCHIVO`).
 *
 * `idCongelado` es un uuid propio del campo dentro de esta ejecución, así que
 * sirve como clave de vínculo: dos campos de imagen en el mismo checklist no
 * terminan compartiendo las fotos entre sí.
 */
/**
 * Firma dibujada en el momento con el dedo (o el mouse). El trazo se guarda
 * como PNG dentro de la respuesta. Una vez firmada, muestra el trazo y quién
 * firmó; si el campo sigue abierto, se puede volver a firmar.
 */
function CampoFirma({
  titulo,
  valor,
  cerrada,
  onResponder,
}: {
  titulo: string;
  valor: unknown;
  cerrada: boolean;
  onResponder: (valor: unknown) => void;
}): JSX.Element {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const trazando = useRef(false);
  const [hayTrazo, setHayTrazo] = useState(false);
  const [firmante, setFirmante] = useState('');

  const firmada = firmaSchema.safeParse(valor).success ? (valor as Firma) : null;

  if (firmada) {
    return (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>
        <div style={{ border: '1px solid #d9dde3', borderRadius: 8, padding: 8, maxWidth: 420, background: 'var(--panel)' }}>
          <img src={firmada.trazo} alt={`Firma de ${firmada.firmante}`} style={{ width: '100%', display: 'block' }} />
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: '#5b6470' }}>
          Firmado <strong>{firmada.firmante}</strong> por {formatearFechaHora(firmada.fechaHora)}
        </div>
        {!cerrada ? (
          <button
            type="button"
            className="boton secundario"
            style={{ marginTop: 8 }}
            onClick={() => {
              setFirmante('');
              setHayTrazo(false);
              onResponder(null);
            }}
          >
            Firmar de nuevo
          </button>
        ) : null}
      </div>
    );
  }

  if (cerrada) return <div style={{ color: '#5b6470', fontSize: 13 }}>Sin firma</div>;

  // Coordenadas del lienzo: el CSS puede estirarlo, así que se escala el punto.
  const punto = (e: { currentTarget: HTMLCanvasElement; clientX: number; clientY: number }) => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (e.currentTarget.width / r.width),
      y: (e.clientY - r.top) * (e.currentTarget.height / r.height),
    };
  };

  const ctx = () => lienzo.current?.getContext('2d') ?? null;

  const limpiar = () => {
    const c = lienzo.current;
    if (c) ctx()?.clearRect(0, 0, c.width, c.height);
    setHayTrazo(false);
  };

  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>
      <canvas
        ref={lienzo}
        width={600}
        height={180}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          trazando.current = true;
          const p = punto(e);
          const c = ctx();
          if (!c) return;
          c.lineWidth = 3;
          c.lineCap = 'round';
          c.lineJoin = 'round';
          c.strokeStyle = '#111';
          c.beginPath();
          c.moveTo(p.x, p.y);
          setHayTrazo(true);
        }}
        onPointerMove={(e) => {
          if (!trazando.current) return;
          const p = punto(e);
          ctx()?.lineTo(p.x, p.y);
          ctx()?.stroke();
        }}
        onPointerUp={() => {
          trazando.current = false;
        }}
        onPointerCancel={() => {
          trazando.current = false;
        }}
        style={{
          width: '100%',
          maxWidth: 420,
          aspectRatio: '600 / 180',
          touchAction: 'none',
          background: 'var(--panel)',
          border: '1px solid #d9dde3',
          borderRadius: 8,
          cursor: 'crosshair',
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <input
          value={firmante}
          onChange={(e) => setFirmante(e.target.value)}
          placeholder="Nombre de quien firma"
          style={{ ...estiloEntrada, maxWidth: 280 }}
        />
        <button type="button" className="boton secundario" disabled={!hayTrazo} onClick={limpiar}>
          Limpiar
        </button>
        <button
          type="button"
          className="boton"
          disabled={!hayTrazo || !firmante.trim()}
          onClick={() => {
            const trazo = lienzo.current?.toDataURL('image/png');
            if (!trazo) return;
            onResponder({ firmante: firmante.trim(), trazo, fechaHora: new Date().toISOString() });
          }}
        >
          Confirmar firma
        </button>
      </div>
    </div>
  );
}

function CampoImagen({
  idCongelado,
  cerrada,
  onResponder,
}: {
  idCongelado: string;
  valor: unknown;
  cerrada: boolean;
  onResponder: (valor: unknown, adjuntos?: string[]) => void;
}): JSX.Element {
  const [fotos, setFotos] = useState<AdjuntoImagen[] | null>(null);

  const { data } = useQuery({
    queryKey: ['adjuntos', 'RESPUESTA_CAMPO', idCongelado],
    queryFn: () =>
      api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'RESPUESTA_CAMPO', entidadId: idCongelado })}`),
  });

  const actuales = fotos ?? data ?? [];

  if (cerrada) {
    return actuales.length > 0 ? <GaleriaImagenes fotos={actuales} /> : <p className="campo-nota">Sin fotos.</p>;
  }

  return (
    <SubidaImagenes
      etiqueta="Fotos"
      valor={actuales}
      alCambiar={(nuevas) => {
        setFotos(nuevas);
        const ids = nuevas.map((f) => f.id);
        onResponder(ids, ids);
      }}
    />
  );
}
